/**
 * QueryTool — zakładka zapytania w przeglądarce bazy (tylko odczyt).
 *
 * Pasek: Wykonaj (F5 / Ctrl+Enter — zaznaczenie albo całość), Wyjaśnij (F7, EXPLAIN bez
 * ANALYZE), limit wierszy, Formatuj, Kopiuj. Pod edytorem: Wynik danych / Komunikaty /
 * Wyjaśnienie. Historia ostatnich zapytań per tenant w localStorage (wygoda, nie audyt —
 * audyt pisze serwer).
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, FileDown, History, Play, ScrollText, ShieldCheck, WandSparkles } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import {
  LazySqlEditor,
  type SqlEditorDiagnostic,
  type SqlEditorHandle,
} from '@/components/ui/LazySqlEditor';
import { useToast } from '@/contexts/ToastContext';
import { DbQueryFailure, runDbQuery } from '@/lib/db-browser/api';
import { toCsv, toTsv } from '@/lib/db-browser/export';
import { addHistory, type QueryHistoryEntry, readHistory } from '@/lib/db-browser/history';
import {
  DB_QUERY_LIMITS,
  type DbQueryError,
  type DbQueryLimit,
  type DbQueryResult,
} from '@/lib/db-browser/types';
import { cn } from '@/lib/utils';

import { ResultsGrid } from './ResultsGrid';

export interface QueryToolHandle {
  insertText: (text: string) => void;
}

interface QueryToolProps {
  tenantId: string;
  sql: string;
  onSqlChange: (sql: string) => void;
  completionSchema: Record<string, string[]>;
  /** Nazwa kontekstu w pasku (`schemat@tenant`). */
  contextLabel: string;
  /** Uruchom od razu po zamontowaniu (np. „Wyświetl dane → pierwsze 100"). */
  autoRun?: boolean;
  initialLimit?: DbQueryLimit;
}

type ResultTab = 'data' | 'messages' | 'plan';

interface RunState {
  result: DbQueryResult | null;
  error: DbQueryError | null;
  messages: string[];
  ranAt: Date | null;
}

const EMPTY: RunState = { result: null, error: null, messages: [], ranAt: null };

/** Zakres podkreślenia błędu: od pozycji do końca słowa. */
function diagnosticFor(err: DbQueryError, sql: string, offset: number): SqlEditorDiagnostic[] {
  if (!err.position) return [];
  const from = offset + err.position - 1;
  const rest = sql.slice(err.position - 1);
  const word = /^[\w."]+/.exec(rest)?.[0]?.length ?? 1;
  return [{ from, to: from + word, message: err.message }];
}

function downloadFile(name: string, content: string, mime: string) {
  const blob = new Blob(['﻿', content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export const QueryTool = forwardRef<QueryToolHandle, QueryToolProps>(function QueryTool(
  { tenantId, sql, onSqlChange, completionSchema, contextLabel, autoRun, initialLimit },
  ref,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const editorRef = useRef<SqlEditorHandle>(null);
  const [limit, setLimit] = useState<DbQueryLimit>(initialLimit ?? 1000);
  const [running, setRunning] = useState(false);
  const [state, setState] = useState<RunState>(EMPTY);
  const [diagnostics, setDiagnostics] = useState<SqlEditorDiagnostic[]>([]);
  const [resultTab, setResultTab] = useState<ResultTab>('data');
  const [editorTab, setEditorTab] = useState<'query' | 'history'>('query');
  const [history, setHistory] = useState<QueryHistoryEntry[]>(() => readHistory(tenantId));
  const autoRan = useRef(false);

  useImperativeHandle(ref, () => ({
    insertText: (text) => editorRef.current?.insertAtCursor(text),
  }));

  const execute = useCallback(
    async (text: string, offset: number, explain: boolean) => {
      if (!text.trim() || running) return;
      setRunning(true);
      setDiagnostics([]);
      const started = new Date();
      try {
        const result = await runDbQuery(tenantId, { sql: text, limit, explain });
        const messages = [
          t('admin.dbBrowser.messages.readOnly'),
          t('admin.dbBrowser.messages.rows', { count: result.rows.length }),
          ...result.notices,
          t('admin.dbBrowser.messages.done', { ms: result.durationMs }),
        ];
        setState({ result, error: null, messages, ranAt: started });
        setResultTab(explain && result.plan ? 'plan' : 'data');
        setHistory(
          addHistory(tenantId, {
            sql: text,
            at: started.toISOString(),
            ok: true,
            rows: result.rows.length,
            ms: result.durationMs,
          }),
        );
      } catch (err) {
        const detail: DbQueryError =
          err instanceof DbQueryFailure
            ? err.detail
            : { error: 'SQL_EXECUTION_FAILED', message: t('admin.dbBrowser.errors.network') };
        setState({ result: null, error: detail, messages: [detail.message], ranAt: started });
        setDiagnostics(diagnosticFor(detail, text, offset));
        setResultTab('data');
        setHistory(
          addHistory(tenantId, { sql: text, at: started.toISOString(), ok: false, rows: 0, ms: 0 }),
        );
      } finally {
        setRunning(false);
      }
    },
    [limit, running, t, tenantId],
  );

  const runFromEditor = (explain: boolean) => {
    const r = editorRef.current?.getRunnable() ?? { sql, offset: 0 };
    void execute(r.sql, r.offset, explain);
  };

  // Auto-uruchomienie dla zakładek otwartych z menu „Wyświetl dane" — raz, przy montażu.
  useEffect(() => {
    if (!autoRun || autoRan.current) return;
    autoRan.current = true;
    void execute(sql, 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tylko przy pierwszym montażu
  }, []);

  const format = async () => {
    const { format: fmt } = await import('sql-formatter');
    try {
      onSqlChange(fmt(sql, { language: 'postgresql', keywordCase: 'upper', tabWidth: 2 }));
    } catch {
      toast.show({ variant: 'warning', title: t('admin.dbBrowser.toolbar.formatFailed') });
    }
  };

  const copy = async (text: string, okKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show({ variant: 'success', title: t(okKey) });
    } catch {
      toast.show({ variant: 'error', title: t('admin.dbBrowser.copyFailed') });
    }
  };

  const result = state.result;
  const maskedCount = result?.columns.filter((c) => c.masked).length ?? 0;
  const limitOptions = useMemo(
    () =>
      DB_QUERY_LIMITS.map((l) => ({
        value: String(l),
        label: t('admin.dbBrowser.toolbar.limitOption', { count: l }),
      })),
    [t],
  );

  const resultTabs: { id: ResultTab; label: string }[] = [
    { id: 'data', label: t('admin.dbBrowser.result.tabs.data') },
    { id: 'messages', label: t('admin.dbBrowser.result.tabs.messages') },
    { id: 'plan', label: t('admin.dbBrowser.result.tabs.plan') },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="toolbar"
        aria-label={t('admin.dbBrowser.toolbar.label')}
        className="flex flex-wrap items-center gap-1.5 border-b border-border-secondary bg-bg-primary px-2 py-1.5"
      >
        <Button
          size="sm"
          leftIcon={<Play size={13} aria-hidden="true" />}
          loading={running}
          onClick={() => runFromEditor(false)}
          title={t('admin.dbBrowser.toolbar.runHint')}
        >
          {t('admin.dbBrowser.toolbar.run')}
          <span className="font-normal opacity-80">F5</span>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<ScrollText size={14} aria-hidden="true" />}
          disabled={running}
          onClick={() => runFromEditor(true)}
          title={t('admin.dbBrowser.toolbar.explainHint')}
        >
          {t('admin.dbBrowser.toolbar.explain')}
          <span className="font-normal text-text-tertiary">F7</span>
        </Button>
        <span aria-hidden="true" className="mx-1 h-5 w-px bg-border-secondary" />
        <Dropdown
          aria-label={t('admin.dbBrowser.toolbar.limit')}
          value={String(limit)}
          onValueChange={(v) => setLimit(Number(v) as DbQueryLimit)}
          options={limitOptions}
          className="h-8 w-36 text-[13px]"
        />
        <span aria-hidden="true" className="mx-1 h-5 w-px bg-border-secondary" />
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<WandSparkles size={14} aria-hidden="true" />}
          onClick={() => void format()}
        >
          {t('admin.dbBrowser.toolbar.format')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Copy size={14} aria-hidden="true" />}
          onClick={() => void copy(sql, 'admin.dbBrowser.toolbar.sqlCopied')}
        >
          {t('admin.dbBrowser.toolbar.copySql')}
        </Button>
        <div className="flex-1" />
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
          <ShieldCheck size={12} aria-hidden="true" />
          {t('admin.dbBrowser.readOnlyBadge')}
        </span>
        <span className="rounded bg-bg-secondary px-2 py-0.5 font-mono text-xs text-text-secondary">
          {contextLabel}
        </span>
      </div>

      <div
        role="tablist"
        aria-label={t('admin.dbBrowser.editor.tabsLabel')}
        className="flex gap-4 border-b border-border-secondary px-3"
      >
        {(['query', 'history'] as const).map((id) => (
          <Button
            key={id}
            variant="ghost"
            role="tab"
            aria-selected={editorTab === id}
            onClick={() => setEditorTab(id)}
            className={cn(
              'h-8 rounded-none border-b-2 px-0 text-xs hover:bg-transparent',
              editorTab === id
                ? 'border-accent-600 font-semibold text-accent-700'
                : 'border-transparent font-normal text-text-secondary',
            )}
            leftIcon={id === 'history' ? <History size={12} aria-hidden="true" /> : undefined}
          >
            {t(`admin.dbBrowser.editor.${id}`)}
          </Button>
        ))}
      </div>

      <div className="h-[240px] min-h-[120px] shrink-0 resize-y overflow-hidden border-b border-border-secondary">
        {editorTab === 'query' ? (
          <LazySqlEditor
            ref={editorRef}
            value={sql}
            onChange={onSqlChange}
            schema={completionSchema}
            diagnostics={diagnostics}
            onRun={(text, offset) => void execute(text, offset, false)}
            onExplain={(text, offset) => void execute(text, offset, true)}
            height="100%"
            aria-label={t('admin.dbBrowser.editor.label')}
          />
        ) : (
          <QueryHistoryList
            entries={history}
            onPick={(entry) => {
              onSqlChange(entry.sql);
              setEditorTab('query');
            }}
          />
        )}
      </div>

      <div
        role="tablist"
        aria-label={t('admin.dbBrowser.result.tabsLabel')}
        className="flex border-b border-border-secondary bg-bg-secondary px-1"
      >
        {resultTabs.map((tab) => (
          <Button
            key={tab.id}
            variant="ghost"
            role="tab"
            aria-selected={resultTab === tab.id}
            onClick={() => setResultTab(tab.id)}
            className={cn(
              'h-8 rounded-none border-t-2 px-3.5 text-[13px]',
              resultTab === tab.id
                ? 'border-accent-600 bg-bg-primary font-semibold'
                : 'border-transparent font-normal text-text-secondary',
            )}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden bg-bg-primary">
        {resultTab === 'data' && (
          <>
            {state.error && (
              <div className="p-3">
                <Alert tone="error" title={t(`admin.dbBrowser.errors.${state.error.error}.title`)}>
                  {t(`admin.dbBrowser.errors.${state.error.error}.body`)}
                </Alert>
              </div>
            )}
            {!state.error && !result && !running && (
              <p className="p-4 text-sm text-text-tertiary">{t('admin.dbBrowser.result.empty')}</p>
            )}
            {!state.error && result && result.columns.length > 0 && (
              <div className="absolute inset-0">
                <ResultsGrid columns={result.columns} rows={result.rows} />
              </div>
            )}
          </>
        )}
        {resultTab === 'messages' && (
          <div className="h-full overflow-auto p-3 font-mono text-xs leading-5 text-text-secondary">
            {state.messages.length === 0
              ? t('admin.dbBrowser.result.noMessages')
              : state.messages.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        {resultTab === 'plan' && (
          <pre className="h-full overflow-auto p-3 font-mono text-xs leading-5 text-text-secondary">
            {result?.plan ?? t('admin.dbBrowser.result.noPlan')}
          </pre>
        )}
      </div>

      {/* pr-16: miejsce na pływający przycisk zgłaszania błędów w prawym dolnym rogu. */}
      <footer className="flex flex-wrap items-center gap-4 border-t border-border-secondary bg-bg-secondary py-1 pl-3 pr-16 text-xs text-text-secondary">
        {result ? (
          <>
            <span>{t('admin.dbBrowser.footer.rows', { count: result.rows.length })}</span>
            <span>{t('admin.dbBrowser.footer.duration', { ms: result.durationMs })}</span>
            {result.truncated && (
              <span className="text-amber-700 dark:text-amber-400">
                {t('admin.dbBrowser.footer.truncated', { limit })}
              </span>
            )}
            {maskedCount > 0 && (
              <span className="text-amber-700 dark:text-amber-400">
                {t('admin.dbBrowser.footer.masked', { count: maskedCount })}
              </span>
            )}
          </>
        ) : (
          <span>
            {running ? t('admin.dbBrowser.footer.running') : t('admin.dbBrowser.footer.idle')}
          </span>
        )}
        <div className="flex-1" />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          leftIcon={<Copy size={12} aria-hidden="true" />}
          disabled={!result?.rows.length}
          onClick={() => result && void copy(toTsv(result), 'admin.dbBrowser.footer.copied')}
        >
          {t('admin.dbBrowser.footer.copy')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          leftIcon={<FileDown size={12} aria-hidden="true" />}
          disabled={!result?.rows.length}
          onClick={() =>
            result &&
            downloadFile(
              `wynik-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`,
              toCsv(result),
              'text/csv;charset=utf-8',
            )
          }
        >
          {t('admin.dbBrowser.footer.csv')}
        </Button>
      </footer>
    </div>
  );
});

function QueryHistoryList({
  entries,
  onPick,
}: {
  entries: QueryHistoryEntry[];
  onPick: (e: QueryHistoryEntry) => void;
}) {
  const { t } = useTranslation();
  if (!entries.length) {
    return <p className="p-4 text-sm text-text-tertiary">{t('admin.dbBrowser.history.empty')}</p>;
  }
  return (
    <ul
      className="h-full divide-y divide-border-secondary overflow-auto"
      aria-label={t('admin.dbBrowser.editor.history')}
    >
      {entries.map((e) => (
        <li key={e.at + e.sql}>
          <Button
            variant="ghost"
            onClick={() => onPick(e)}
            className="h-auto w-full flex-col items-start gap-0.5 rounded-none px-3 py-1.5 text-left font-normal"
          >
            <span className="w-full truncate font-mono text-xs text-text-primary">
              {e.sql.replace(/\s+/g, ' ')}
            </span>
            <span
              className={cn(
                'text-[11px]',
                e.ok ? 'text-text-tertiary' : 'text-red-600 dark:text-red-400',
              )}
            >
              {new Date(e.at).toLocaleString('pl-PL')} ·{' '}
              {e.ok
                ? t('admin.dbBrowser.history.ok', { count: e.rows, ms: e.ms })
                : t('admin.dbBrowser.history.failed')}
            </span>
          </Button>
        </li>
      ))}
    </ul>
  );
}
